import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Plus, Trash2, RotateCcw, PackageX } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { formatCurrency } from "@/lib/currency";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type PurchaseReturn = {
  id: number;
  returnNumber: string;
  purchaseId: number | null;
  supplierId: number;
  supplierName: string | null;
  totalAmount: number;
  reason: string | null;
  notes: string | null;
  status: string;
  items: Array<{ productId: number; productName: string; quantity: number; unitCost: number }>;
  createdAt: string;
};

type Supplier = { id: number; name: string };
type Product = { id: number; name: string; sku: string; costPrice: number; stock: number };

export default function PurchaseReturns() {
  const qc = useQueryClient();
  const { toast } = useToast();

  const { data: returns = [], isLoading } = useQuery<PurchaseReturn[]>({
    queryKey: ["purchase-returns"],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/purchase-returns`, { credentials: "include" });
      return r.json();
    },
  });

  const { data: suppliers = [] } = useQuery<Supplier[]>({
    queryKey: ["suppliers"],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/suppliers`, { credentials: "include" });
      return r.json();
    },
  });

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ["products"],
    queryFn: async () => {
      const r = await fetch(`${BASE}/api/products`, { credentials: "include" });
      return r.json();
    },
  });

  const [isOpen, setIsOpen] = useState(false);
  const [supplierId, setSupplierId] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [returnItems, setReturnItems] = useState<Array<{ productId: number; productName: string; quantity: number; unitCost: number }>>([]);
  const [productSearch, setProductSearch] = useState("");

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(productSearch.toLowerCase()) || p.sku.toLowerCase().includes(productSearch.toLowerCase())
  ).slice(0, 8);

  const addItem = (p: Product) => {
    setReturnItems(prev => {
      const exists = prev.find(i => i.productId === p.id);
      if (exists) return prev.map(i => i.productId === p.id ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { productId: p.id, productName: p.name, quantity: 1, unitCost: p.costPrice }];
    });
    setProductSearch("");
  };

  const removeItem = (productId: number) => setReturnItems(prev => prev.filter(i => i.productId !== productId));
  const updateQty = (productId: number, qty: number) => setReturnItems(prev => prev.map(i => i.productId === productId ? { ...i, quantity: Math.max(1, qty) } : i));
  const updateCost = (productId: number, cost: number) => setReturnItems(prev => prev.map(i => i.productId === productId ? { ...i, unitCost: cost } : i));

  const total = returnItems.reduce((s, i) => s + i.quantity * i.unitCost, 0);

  const reset = () => { setSupplierId(""); setReason(""); setNotes(""); setReturnItems([]); setProductSearch(""); };

  const create = useMutation({
    mutationFn: async () => {
      const r = await fetch(`${BASE}/api/purchase-returns`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ supplierId: Number(supplierId), items: returnItems, reason, notes }),
      });
      if (!r.ok) throw new Error("Failed");
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["purchase-returns"] });
      qc.invalidateQueries({ queryKey: ["products"] });
      setIsOpen(false);
      reset();
      toast({ title: "Return recorded", description: "Stock has been adjusted." });
    },
    onError: () => toast({ title: "Failed to record return", variant: "destructive" }),
  });

  const remove = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/purchase-returns/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed to delete return");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["purchase-returns"] }),
    onError: () => toast({ title: "Failed to delete return", variant: "destructive" }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Purchase Returns</h1>
          <p className="text-muted-foreground mt-1">Return damaged or incorrect goods back to suppliers. Stock is adjusted automatically.</p>
        </div>
        <Dialog open={isOpen} onOpenChange={o => { setIsOpen(o); if (!o) reset(); }}>
          <DialogTrigger asChild>
            <Button><Plus className="w-4 h-4 mr-2" /> New Return</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Record Purchase Return</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Supplier <span className="text-destructive">*</span></label>
                  <Select value={supplierId} onValueChange={setSupplierId}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                    <SelectContent>
                      {suppliers.map(s => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Reason</label>
                  <Input className="mt-1" placeholder="e.g. Damaged, Wrong item" value={reason} onChange={e => setReason(e.target.value)} />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Search & Add Items</label>
                <Input
                  placeholder="Search product by name or SKU..."
                  value={productSearch}
                  onChange={e => setProductSearch(e.target.value)}
                />
                {productSearch && filteredProducts.length > 0 && (
                  <div className="border rounded-lg mt-1 divide-y max-h-48 overflow-y-auto">
                    {filteredProducts.map(p => (
                      <button key={p.id} type="button" className="w-full text-left px-3 py-2 hover:bg-muted text-sm flex justify-between" onClick={() => addItem(p)}>
                        <span>{p.name}</span>
                        <span className="text-muted-foreground">{p.sku} · cost {formatCurrency(p.costPrice)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {returnItems.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead className="w-24">Qty</TableHead>
                        <TableHead className="w-32">Unit Cost (₦)</TableHead>
                        <TableHead className="w-24 text-right">Total</TableHead>
                        <TableHead className="w-10" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {returnItems.map(item => (
                        <TableRow key={item.productId}>
                          <TableCell className="font-medium">{item.productName}</TableCell>
                          <TableCell><Input type="number" min={1} value={item.quantity} onChange={e => updateQty(item.productId, Number(e.target.value))} className="h-8 w-20" /></TableCell>
                          <TableCell><Input type="number" min={0} step="0.01" value={item.unitCost} onChange={e => updateCost(item.productId, Number(e.target.value))} className="h-8 w-28" /></TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(item.quantity * item.unitCost)}</TableCell>
                          <TableCell><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeItem(item.productId)}><Trash2 className="w-3.5 h-3.5" /></Button></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="flex justify-end px-4 py-2 bg-muted/30 border-t font-semibold text-sm">
                    Total return value: {formatCurrency(total)}
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium">Internal notes</label>
                <Textarea className="mt-1" rows={2} placeholder="Any additional notes..." value={notes} onChange={e => setNotes(e.target.value)} />
              </div>

              <Button
                className="w-full"
                disabled={create.isPending || !supplierId || returnItems.length === 0}
                onClick={() => create.mutate()}
              >
                {create.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Record Return & Adjust Stock
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Return #</TableHead>
              <TableHead>Supplier</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Total Value</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></TableCell></TableRow>
            ) : returns.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-14 text-muted-foreground">
                  <PackageX className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p>No purchase returns yet.</p>
                  <p className="text-xs mt-1">Use this when returning damaged or incorrect goods to a supplier.</p>
                </TableCell>
              </TableRow>
            ) : returns.map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs font-semibold">{r.returnNumber}</TableCell>
                <TableCell className="font-medium">{r.supplierName ?? "—"}</TableCell>
                <TableCell>
                  <span className="text-sm text-muted-foreground">{r.items.length} item{r.items.length !== 1 ? "s" : ""}</span>
                </TableCell>
                <TableCell className="font-medium">{formatCurrency(r.totalAmount)}</TableCell>
                <TableCell>{r.reason ? <Badge variant="outline">{r.reason}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{new Date(r.createdAt).toLocaleDateString("en-NG")}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => { if (confirm("Delete this return record?")) remove.mutate(r.id); }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
